import type { TaskDoorRoute, TaskDoorRouteRequest } from "@bash/shared";
import { isTaskDoorControl } from "@bash/shared";
import { apiUrl } from "@/lib/api/base";
import { getOptionalAccessToken } from "@/lib/api/client";

/** The client's deadline (spec 2026-09-20 §3.2; R-L20): past it the words ride the task, as today. 1000 ms holds a Delhi round trip on top of a ~500 ms server span; the drill records the real time. */
export const ROUTE_DEADLINE_MS = 1000;

const FAULT: TaskDoorRoute = { to: "task", why: "fault" };

/** The body by its structured fields, never by matching text: a `to` this client does not know is a fault. */
function isRoute(b: unknown): b is TaskDoorRoute {
  if (typeof b !== "object" || b === null) return false;
  const r = b as Record<string, unknown>;
  if (r.to === "task") return typeof r.why === "string";
  if (r.to === "composer") return true;
  if (r.to === "control")
    return isTaskDoorControl(r.control) && typeof r.words === "string";
  return false;
}

/**
 * POST /api/composer/route (spec 2026-09-20 §3.2): which rail a plain sentence
 * typed on a task's screen rides. NEVER THROWS and never waits past the
 * deadline: no session, a session read past the deadline, a rejected fetch, a
 * non-OK status, an unreadable body and a body of the wrong shape each answer
 * `{ to: 'task', why: 'fault' }`, and the caller sends the words down the
 * classic lane exactly as it does today.
 */
export async function routeTaskDoor(
  req: TaskDoorRouteRequest
): Promise<TaskDoorRoute> {
  // The deadline covers the token read too (review T7 #4): a session refresh
  // that lands past it is a fault, so the words ride the task at once instead
  // of waiting on auth latency.
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), ROUTE_DEADLINE_MS);
  try {
    const token = await getOptionalAccessToken().catch(() => null);
    if (!token || ac.signal.aborted) return FAULT;
    const res = await fetch(apiUrl("/api/composer/route"), {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(req),
      signal: ac.signal,
    });
    if (!res.ok) return FAULT;
    const body: unknown = await res.json();
    return isRoute(body) ? body : FAULT;
  } catch {
    return FAULT;
  } finally {
    clearTimeout(timer);
  }
}
